import { useEffect, useRef, useState } from 'react'
import type { MsgDeskPageProps } from '../../MsgDeskApp'
import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { EnterPinForm, ShellOverlays } from '@/deskUiSkeletons/Shared/Overlays'
import { PinDialog } from '@/deskUiSkeletons/Shared/PinDialog'
import { editorOpenActionForMode } from '../../routing/editor'
import { standardEditorModeFromSegment, type StandardEditorMode } from '../../routing/standard'
import {
  applyStandardMode,
  isStandardEditorPathname,
  pushStandardEditorUrl,
  replaceStandardModeUrl,
} from '../../host/deskRoutes'
import { SitePage } from './SitePage'
import { SiteMenu } from './SiteMenu'
import { SiteSharedSurfaceProvider } from './Shared/SharedSurface'
import { SiteEditorSurfaceProvider } from './editor/EditorSurface'
import { SiteEditorTemplate } from './editor/SiteEditorTemplate'

export function SiteRoutedPage({
  isEditorOpen = false,
  editorMode,
  editorBridgeReady = false,
  initialEditorMode,
}: MsgDeskPageProps & { initialEditorMode?: StandardEditorMode }) {
  const { state } = useSubsStore()
  const editorActive = isEditorOpen || state.renderProjection.mode === 'editor'
  const initialEditorOpened = useRef(false)
  const editorWasOpen = useRef(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  useEffect(() => {
    if (!initialEditorMode || !editorBridgeReady || initialEditorOpened.current) return
    initialEditorOpened.current = true
    dispatchEditorAction({ action: editorOpenActionForMode(initialEditorMode) })
  }, [editorBridgeReady, initialEditorMode])

  useEffect(() => {
    if (isEditorOpen) {
      editorWasOpen.current = true
      if (editorMode === 'update_msg' || editorMode === 'new_msg') pushStandardEditorUrl(editorMode)
      return
    }

    if (editorWasOpen.current && isStandardEditorPathname(window.location.pathname)) {
      editorWasOpen.current = false
      replaceStandardModeUrl('msg')
    }
  }, [editorMode, isEditorOpen])

  useEffect(() => {
    if (editorActive || !settingsOpen) return
    setSettingsOpen(false)
  }, [editorActive, settingsOpen])

  useEffect(() => {
    const handlePopState = () => {
      const segment = window.location.pathname.split('/').filter(Boolean).pop() || ''
      const routeEditorMode = standardEditorModeFromSegment(segment)
      if (routeEditorMode && editorBridgeReady) {
        dispatchEditorAction({ action: editorOpenActionForMode(routeEditorMode) })
        return
      }
      applyStandardMode('msg')
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [editorBridgeReady])

  return (
    <SiteSharedSurfaceProvider>
      <SiteEditorSurfaceProvider>
        <ShellOverlays />
        <div {...legacyHookAttrs('page.root', 'md-site-page min-h-full w-full bg-white')}>
          <div {...legacyHookAttrs('page.table', 'md-site-page-table min-h-full w-full bg-white')}>
            <div {...legacyHookAttrs('page.header', 'hidden')} hidden aria-hidden="true">
              <div {...legacyHookAttrs('page.headerCell', 'hidden')} />
            </div>
            <div {...legacyHookAttrs('page.workspaceCell', 'min-h-full w-full bg-white')}>
              <SitePage active={!editorActive} />
              <SiteEditorTemplate active={editorActive} editorMode={isEditorOpen ? editorMode : ''} settingsOpen={settingsOpen} onCloseSettings={() => setSettingsOpen(false)} />
              <div {...legacyHookAttrs('workspace.attachments', 'hidden')} hidden aria-hidden="true" />
              <div {...legacyHookAttrs('workspace.favs', 'hidden')} hidden aria-hidden="true" />
              <div {...legacyHookAttrs('workspace.drafts', 'hidden')} hidden aria-hidden="true" />
              <div {...legacyHookAttrs('workspace.trash', 'hidden')} hidden aria-hidden="true" />
            </div>
            <div {...legacyHookAttrs('page.accentCell', 'hidden')} hidden aria-hidden="true" />
            <div {...legacyHookAttrs('page.rightRail', 'hidden')} hidden aria-hidden="true" />
            <div {...legacyHookAttrs('page.footer', 'hidden')} hidden aria-hidden="true" />
          </div>
        </div>

        <SiteMenu
          isEditorOpen={editorActive}
          settingsOpen={settingsOpen}
          onToggleSettings={() => setSettingsOpen(open => !open)}
        />

        <div className="hidden" hidden aria-hidden="true"><EnterPinForm /></div>
        <PinDialog />
      </SiteEditorSurfaceProvider>
    </SiteSharedSurfaceProvider>
  )
}
